/* eslint-disable no-param-reassign */
/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState, useRef } from 'react';
import { Button, Form, Input, notification, Popover, Spin, Result } from 'antd';
import {
  EyeOutlined,
  EyeInvisibleOutlined,
  LockOutlined,
  CheckCircleFilled,
} from '@ant-design/icons';
import { resetPasswordSubmit } from '../services/auth';
import history from '../services/history';
import * as constants from '../utils/constants';
import { passwordPolicy } from '../utils/passwordPolicy';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';

const EMAIL_NOTIFICATION_URL =
  process.env.REACT_APP_EMAIL_NOTIFICATION_URL || 'http://localhost:3002';

const ResetPasswordForm = () => {
  const [passForm] = Form.useForm();
  const [validatingStatus, setValidatingStatus] = useState('');
  const [validateVisible, setValidateVisible] = useState(false);
  const [validatingStatusConfirm, setValidatingStatusConfirm] = useState('');
  const [newPassword, setNewPassword] = useState('');

  // Invite token states
  const [loading, setLoading] = useState(true);
  const [inviteValid, setInviteValid] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteError, setInviteError] = useState('');
  const [isInviteFlow, setIsInviteFlow] = useState(false);

  // OTP states
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // OTP input refs
  const otpRefs = useRef([]);

  const location = useLocation();

  // Check for invite token on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const inviteToken = params.get('invite');

    if (inviteToken) {
      setIsInviteFlow(true);
      verifyInviteToken(inviteToken);
    } else {
      setLoading(false);
      setIsInviteFlow(false);
    }
  }, [location.search]);

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const verifyInviteToken = async (token) => {
    try {
      const response = await axios.get(`${EMAIL_NOTIFICATION_URL}/pih/verify-invite`, {
        params: { invite: token },
      });

      if (response.data?.data?.valid) {
        setInviteValid(true);
        setInviteEmail(response.data.data.payload?.email || '');
      } else {
        setInviteValid(false);
        setInviteError(response.data?.data?.error || 'Invalid or expired invitation link');
      }
    } catch (error) {
      console.error('Error verifying invite token:', error);
      setInviteValid(false);
      setInviteError('Unable to verify invitation link. Please try again or contact support.');
    } finally {
      setLoading(false);
    }
  };

  const sendOtp = async () => {
    if (!inviteEmail) {
      notification.error({ message: 'Email not found. Please try again.' });
      return;
    }

    setOtpLoading(true);
    try {
      await axios.post(`${EMAIL_NOTIFICATION_URL}/pih/send-otp`, {
        email: inviteEmail,
      });

      setOtpSent(true);
      setResendTimer(30);
      notification.success({ message: 'OTP sent to your email' });
    } catch (error) {
      console.error('Error sending OTP:', error);
      notification.error({ message: 'Failed to send OTP. Please try again.' });
    } finally {
      setOtpLoading(false);
    }
  };

  const handleSetPassword = async () => {
    try {
      const values = await passForm.validateFields();

      if (values.password !== values.password_confirm) {
        notification.warning({ message: 'Passwords do not match' });
        return;
      }

      if (!otpCode || otpCode.length !== 6) {
        notification.warning({ message: 'Please enter a valid 6-digit OTP' });
        return;
      }

      setSubmitting(true);

      const response = await axios.post(`${EMAIL_NOTIFICATION_URL}/pih/verify-otp-reset`, {
        email: inviteEmail,
        code: otpCode,
        password: values.password,
      });

      if (response.data?.data?.success) {
        notification.success({ message: 'Password set successfully! You can now log in.' });
        history.push('/login');
      } else {
        notification.error({ message: response.data?.data?.message || 'Failed to set password' });
      }
    } catch (error) {
      console.error('Error setting password:', error);
      const errorMsg = error.response?.data?.message || 'Failed to set password. Please try again.';
      notification.error({ message: errorMsg });
    } finally {
      setSubmitting(false);
    }
  };

  // Legacy reset password flow
  const changeTempPass = async (data) => {
    try {
      if (data.password === data.password_confirm) {
        if (data.lastName) {
          data.fullName = `${data.firstName} ${data.lastName}`;
        } else {
          data.fullName = `${data.firstName}`;
        }
        await resetPasswordSubmit(data.password, data.fullName);
        history.push('./login');
      } else {
        notification.warning({
          message: 'Passwords do not match',
        });
      }
    } catch (err) {
      notification.warning({
        message: 'Something went wrong.- Please try again!',
      });
    }
  };

  const validatePass = () => {
    passForm.validateFields().then(async (data) => {
      await changeTempPass(data);
    });
  };

  const validateToNextPassword = (e) => {
    const value = e.target.value;
    if (!value) {
      setValidateVisible(false);
    }
    if (value) {
      setNewPassword(value);
      setValidatingStatus('validating');
      setTimeout(() => {
        if (value.length > 7 && constants.PASSWORD_PATTERN.test(value)) {
          setValidatingStatus('success');
          setValidateVisible(false);
        } else {
          setValidatingStatus('warning');
          setValidateVisible(true);
        }
      }, 500);
    }
  };

  const validateToFirstPassword = (e) => {
    const value = e.target.value;
    if (value) {
      setValidatingStatusConfirm('validating');
      setTimeout(() => {
        if (value.length > 7 && value === newPassword) {
          setValidatingStatusConfirm('success');
        } else {
          setValidatingStatusConfirm('warning');
        }
      }, 500);
    }
  };

  // OTP input handlers
  const handleOtpChange = (index, value) => {
    const sanitizedValue = value.replace(/\D/, '');
    const newOtp = otpCode.split('');
    newOtp[index] = sanitizedValue;
    setOtpCode(newOtp.join(''));

    if (sanitizedValue && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const digits = pastedText.replace(/\D/g, '').slice(0, 6);
    setOtpCode(digits);
    const focusIndex = Math.min(digits.length, 5);
    setTimeout(() => {
      otpRefs.current[focusIndex]?.focus();
    }, 0);
  };

  // Loading state
  if (loading) {
    return (
      <div className="login-form reset-form">
        <div className="loading-state">
          <Spin size="large" />
          <p className="loading-text">Verifying invitation...</p>
        </div>
      </div>
    );
  }

  // Invalid/Expired invite link
  if (isInviteFlow && !inviteValid) {
    return (
      <div className="login-form reset-form">
        <Result
          status="error"
          title={<span style={{ color: '#fff' }}>Invalid or Expired Link</span>}
          subTitle={
            <span style={{ color: '#ffffff99' }}>
              {inviteError ||
                'This invitation link is no longer valid. Please contact your administrator for a new invitation.'}
            </span>
          }
          extra={[
            <Button
              key="login"
              size="large"
              shape="round"
              className="login-form-button"
              onClick={() => history.push('/login')}
            >
              Go to Login
            </Button>,
          ]}
        />
        <span className="login_copy">
          Pioneered by CRC Evans &copy;{new Date().getFullYear()} |{' '}
          <a href="" rel="noreferrer" target="_blank" style={{ pointerEvents: 'none' }}>
            Terms & Conditions
          </a>{' '}
          |{' '}
          <a href="https://www.crcevans.com/privacy-policy/" rel="noreferrer" target="_blank">
            Privacy Policy
          </a>
        </span>
      </div>
    );
  }

  // Valid invite flow - Set Password with OTP
  if (isInviteFlow && inviteValid) {
    return (
      <div className="login-form reset-form invite-flow">
        <LockOutlined
          className="mb-4"
          style={{
            fontSize: '40px',
            paddingRight: '20px',
            color: '#ef314c',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        />
        <h1 className="mb-3 text-center">Set Your Password</h1>
        <p className="subtitle-text">Welcome to CRCE Connect!</p>

        {/* Email Display */}
        <div className="email-display">
          <span className="email-text">{inviteEmail}</span>
          <CheckCircleFilled className="email-verified-icon" />
        </div>

        <Form name="set_password" form={passForm} className="password-form">
          {/* Password Field */}
          <Popover
            placement="bottomRight"
            title={constants.TITLE}
            content={passwordPolicy}
            trigger="focus"
            visible={validateVisible}
          >
            <Form.Item
              name="password"
              rules={[
                { required: true, message: 'Please enter new password.' },
                {
                  pattern: constants.PASSWORD_PATTERN,
                  message: 'Password does not meet requirements',
                },
              ]}
              hasFeedback
              validateStatus={validatingStatus}
            >
              <Input.Password
                size="large"
                className="password-icon"
                placeholder="New password"
                onChange={validateToNextPassword}
                iconRender={(visible) =>
                  visible ? (
                    <EyeOutlined style={{ color: 'grey' }} />
                  ) : (
                    <EyeInvisibleOutlined style={{ color: 'grey' }} />
                  )
                }
              />
            </Form.Item>
          </Popover>

          {/* Confirm Password Field */}
          <Form.Item
            name="password_confirm"
            rules={[{ required: true, message: 'Please confirm your password.' }]}
            hasFeedback
            validateStatus={validatingStatusConfirm}
          >
            <Input.Password
              size="large"
              className="password-icon"
              placeholder="Confirm new password"
              onChange={validateToFirstPassword}
              iconRender={(visible) =>
                visible ? (
                  <EyeOutlined style={{ color: 'grey' }} />
                ) : (
                  <EyeInvisibleOutlined style={{ color: 'grey' }} />
                )
              }
            />
          </Form.Item>

          {/* OTP Section */}
          {!otpSent ? (
            <Form.Item>
              <Button
                size="large"
                shape="round"
                block
                className="login-form-button primary-action"
                loading={otpLoading}
                onClick={sendOtp}
                disabled={validatingStatus !== 'success' || validatingStatusConfirm !== 'success'}
              >
                Send Verification Code
              </Button>
              <p className="otp-hint">A 6-digit code will be sent to your email</p>
            </Form.Item>
          ) : (
            <div className="otp-section">
              <div className="otp-header">
                <span className="otp-label">Enter Verification Code</span>
                <span className="otp-sent-badge">
                  <CheckCircleFilled /> Sent
                </span>
              </div>

              {/* OTP Input Boxes */}
              <div className="otp-input-group">
                {[0, 1, 2, 3, 4, 5].map((index) => (
                  <input
                    key={index}
                    ref={(el) => (otpRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={otpCode[index] || ''}
                    className={`otp-box ${otpCode[index] ? 'filled' : ''}`}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    onPaste={index === 0 ? handleOtpPaste : undefined}
                  />
                ))}
              </div>

              {/* Resend OTP */}
              <div className="resend-section">
                <span className="resend-text">Didn't receive code?</span>
                <Button
                  type="link"
                  size="small"
                  className="resendBtn"
                  disabled={resendTimer > 0}
                  loading={otpLoading}
                  onClick={sendOtp}
                >
                  {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}
                </Button>
              </div>

              {/* Submit Button */}
              <Form.Item>
                <Button
                  size="large"
                  shape="round"
                  block
                  className="login-form-button primary-action"
                  loading={submitting}
                  onClick={handleSetPassword}
                  disabled={otpCode.length !== 6}
                >
                  Verify & Set Password
                </Button>
              </Form.Item>
            </div>
          )}
        </Form>

        <Button block className="login-form-button">
          <Link to="/login">Back to Login</Link>
        </Button>

        <span className="login_copy">
          Pioneered by CRC Evans &copy;{new Date().getFullYear()} |{' '}
          <a href="" rel="noreferrer" target="_blank" style={{ pointerEvents: 'none' }}>
            Terms & Conditions
          </a>{' '}
          |{' '}
          <a href="https://www.crcevans.com/privacy-policy/" rel="noreferrer" target="_blank">
            Privacy Policy
          </a>
        </span>
      </div>
    );
  }

  // Legacy reset password form (no invite token)
  return (
    <div className="login-form reset-form">
      <LockOutlined className="mb-4" style={{ fontSize: '40px', paddingRight: '20px' }} />
      <h1 className="mb-3 text-center">Change Password</h1>
      <Form name="normal_login" initialValues={{ remember: true }} form={passForm}>
        <Popover
          placement="bottomRight"
          title={constants.TITLE}
          content={passwordPolicy}
          trigger="focus"
          visible={validateVisible}
        >
          <Form.Item
            name="password"
            rules={[{ required: true, message: 'Please enter new password.' }]}
            hasFeedback
            validateStatus={validatingStatus}
          >
            <Input.Password
              size="large"
              className="password-icon"
              placeholder="New password"
              onChange={validateToNextPassword}
              iconRender={(visibility) =>
                visibility ? (
                  <EyeOutlined style={{ color: 'grey' }} />
                ) : (
                  <EyeInvisibleOutlined style={{ color: 'grey' }} />
                )
              }
            />
          </Form.Item>
        </Popover>
        <Form.Item
          name="password_confirm"
          rules={[{ required: true, message: 'Please confirm your password.' }]}
          hasFeedback
          validateStatus={validatingStatusConfirm}
        >
          <Input.Password
            size="large"
            className="password-icon"
            placeholder="Confirm new password"
            onChange={validateToFirstPassword}
            iconRender={(visibility) =>
              visibility ? (
                <EyeOutlined style={{ color: 'grey' }} />
              ) : (
                <EyeInvisibleOutlined style={{ color: 'grey' }} />
              )
            }
          />
        </Form.Item>

        <Form.Item>
          <Button
            size="large"
            shape="round"
            block
            htmlType="submit"
            className="login-form-button"
            onClick={validatePass}
          >
            Change Password
          </Button>
        </Form.Item>
        <Button block className="login-form-button">
          <Link to="/login">Back to Login</Link>
        </Button>
      </Form>
      <span className="login_copy">
        Pioneered by CRC Evans &copy;{new Date().getFullYear()} |{' '}
        <a href="" rel="noreferrer" target="_blank" style={{ pointerEvents: 'none' }}>
          Terms & Conditions
        </a>{' '}
        |{' '}
        <a href="https://www.crcevans.com/privacy-policy/" rel="noreferrer" target="_blank">
          Privacy Policy
        </a>
      </span>
    </div>
  );
};

export default ResetPasswordForm;
